import { MatterEmailDetailContent } from "@/components/features/matter/emails/MatterEmailDetailContent";
import {
  buildMatterEmailTableRows,
  type MatterEmailTableRow,
} from "@/components/features/matter/emails/matter-emails-columns";
import {
  Button,
  Card,
  CardHeader,
  Input,
  Tabs,
  TabsList,
  TabsTrigger,
  Text,
} from "@/components/ui";
import { useUser } from "@/components/UserProvider";
import { useGlobalModal } from "@/hooks/useGlobalModal";
import { PAGE_PERMISSION_KEYS } from "@/lib/data/static/permissionOptions";
import type { Matter } from "@/lib/types";
import { cn } from "@/lib/utils/cn";
import { copyToClipboard } from "@/lib/utils/copyToClipboard";
import { convertToLocalFromDatabaseDateTime } from "@/lib/utils/dateUtils";
import { Copy, Inbox, Mail, Search, Send } from "lucide-react";
import dayjs from "dayjs";
import Fuse from "fuse.js";
import { useEffect, useMemo, useState } from "react";

type MatterEmailListTab = "received" | "sent";

/** Neutral gray strip; distinct from the white page. */
const forwardEmailsBannerClassName = cn(
  "flex flex-wrap items-center gap-2 rounded-xl border border-border bg-muted px-3 py-3 text-sm",
);

interface MatterEmailsProps {
  matter: Matter;
  selectedEmailId?: string;
  onSelectedEmailIdChange: (emailId: string | undefined) => void;
}

export const MatterEmails = ({
  matter,
  selectedEmailId,
  onSelectedEmailIdChange,
}: MatterEmailsProps) => {
  const { hasPagePermission, currentUser } = useUser();
  const { openSendMatterEmailModal } = useGlobalModal();
  const hasViewPermission = hasPagePermission(
    PAGE_PERMISSION_KEYS.mattersDetailEmails,
  );
  const [listSearch, setListSearch] = useState("");
  const [activeTab, setActiveTab] = useState<MatterEmailListTab>("received");

  const sortedRows = useMemo(() => {
    const rows = buildMatterEmailTableRows(matter);
    return [...rows].sort((a, b) => {
      const da = a.dateCreated;
      const db = b.dateCreated;
      return (db ?? "").localeCompare(da ?? "");
    });
  }, [matter]);

  const receivedRows = useMemo(
    () => sortedRows.filter((row) => row.direction === "INBOUND"),
    [sortedRows],
  );
  const sentRows = useMemo(
    () => sortedRows.filter((row) => row.direction === "OUTBOUND"),
    [sortedRows],
  );
  const tabRows = activeTab === "received" ? receivedRows : sentRows;

  const filteredRows = useMemo(() => {
    const sanitizedQuery = listSearch.trim();
    if (!sanitizedQuery) return tabRows;

    const searchable = tabRows.map((row) => ({
      ...row,
      fromLine: (row.fromEmails ?? []).join(" "),
      toLine: (row.toEmails ?? []).join(" "),
      ccLine: (row.ccEmails ?? []).join(" "),
    }));

    const fuse = new Fuse(searchable, {
      keys: [
        { name: "subject", weight: 0.7 },
        { name: "fromLine", weight: 0.2 },
        { name: "toLine", weight: 0.1 },
        { name: "ccLine", weight: 0.05 },
      ],
      threshold: 0.35,
      ignoreLocation: true,
      minMatchCharLength: 1,
      includeScore: false,
      useExtendedSearch: false,
    });

    return fuse.search(sanitizedQuery).map((result) => result.item);
  }, [tabRows, listSearch]);

  useEffect(() => {
    const ids = new Set(tabRows.map((r) => r.id));
    if (selectedEmailId && !ids.has(selectedEmailId)) {
      onSelectedEmailIdChange(tabRows[0]?.id);
      return;
    }
    if (!selectedEmailId && tabRows.length > 0) {
      onSelectedEmailIdChange(tabRows[0].id);
    }
  }, [tabRows, selectedEmailId, onSelectedEmailIdChange]);

  const formatListDate = (row: MatterEmailTableRow) => {
    const raw = row.dateCreated;
    if (!raw) return "";
    const local = convertToLocalFromDatabaseDateTime(raw);
    if (!local) return "";
    const d = dayjs(local);
    return d.isValid() ? d.format("MMM D") : "";
  };

  const senderLine = (row: MatterEmailTableRow) => {
    const from = row.fromEmails?.[0]?.trim();
    if (from) return from;
    return "Unknown sender";
  };

  const recipientLine = (row: MatterEmailTableRow) => {
    const to = row.toEmails?.[0]?.trim();
    if (to) return to;
    return "Unknown recipient";
  };

  const listPrimaryLine = (row: MatterEmailTableRow) =>
    activeTab === "sent" ? recipientLine(row) : senderLine(row);

  const hasNoEmails = sortedRows.length === 0;
  const hasNoTabEmails = tabRows.length === 0;
  const activeTabLabel = activeTab === "received" ? "received" : "sent";
  const handleOpenSendEmail = () => {
    openSendMatterEmailModal({
      matterId: matter.id,
      templateData: {
        firm_name: currentUser.firm.name,
      },
      title: "Send email",
      description: "Compose and send an email for this matter.",
    });
  };

  const pageChromeClass =
    "bg-background flex h-full min-h-0 w-full min-w-0 flex-1 flex-col overflow-hidden";

  if (!hasViewPermission) {
    return (
      <div className={`${pageChromeClass} gap-y-4 overflow-y-auto p-4 sm:p-6`}>
        <Card className="w-full rounded-2xl border shadow-none">
          <CardHeader className="space-y-1">
            <Text variant="subHeading">
              You do not have permission to view matter emails.
            </Text>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className={`${pageChromeClass} p-4 sm:p-6`}>
      <div className="flex h-full min-h-0 w-full min-w-0 flex-1 flex-col gap-4 sm:gap-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="space-y-1">
            <Text variant="title">Emails</Text>
            <Text variant="subHeading">
              {receivedRows.length} received · {sentRows.length} sent
            </Text>
          </div>
          <div className="flex w-full flex-col gap-3 sm:ml-auto sm:w-auto sm:items-end">
            <Button type="button" onClick={handleOpenSendEmail}>
              <Mail className="mr-2 h-4 w-4" />
              Send email
            </Button>
            {matter.emailAddress ? (
              <div
                className={cn(
                  forwardEmailsBannerClassName,
                  "w-full max-w-full min-w-0 sm:w-auto",
                )}
              >
                <Mail className="text-muted-foreground h-4 w-4 shrink-0" />
                <Text variant="subHeading" as="span" className="shrink-0">
                  Forward emails to:
                </Text>
                <code className="bg-card border-border/80 max-w-full min-w-0 rounded border px-2 py-0.5 text-xs break-all">
                  {matter.emailAddress}
                </code>
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  className="h-8 w-8 shrink-0"
                  onClick={() => {
                    void copyToClipboard(
                      matter.emailAddress ?? "",
                      "Matter email address",
                    );
                  }}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            ) : null}
          </div>
        </div>

        <div className="border-border bg-card grid min-h-0 min-w-0 flex-1 grid-cols-1 overflow-hidden rounded-xl border md:grid-cols-[minmax(260px,36%)_1fr]">
          <div className="border-border bg-card flex min-h-0 min-w-0 flex-col overflow-x-hidden border-b md:border-r md:border-b-0">
            <div className="border-border bg-card shrink-0 border-b p-3">
              <Tabs
                value={activeTab}
                onValueChange={(value) => {
                  setActiveTab(value as MatterEmailListTab);
                  setListSearch("");
                }}
                className="mb-3"
              >
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="received" className="gap-1.5">
                    <Inbox className="h-4 w-4 shrink-0" />
                    Received
                    <Text
                      variant="caption"
                      as="span"
                      className="bg-muted text-muted-foreground rounded-full px-1.5 py-0.5"
                    >
                      {receivedRows.length}
                    </Text>
                  </TabsTrigger>
                  <TabsTrigger value="sent" className="gap-1.5">
                    <Send className="h-4 w-4 shrink-0" />
                    Sent
                    <Text
                      variant="caption"
                      as="span"
                      className="bg-muted text-muted-foreground rounded-full px-1.5 py-0.5"
                    >
                      {sentRows.length}
                    </Text>
                  </TabsTrigger>
                </TabsList>
              </Tabs>
              <Text
                variant="caption"
                color="muted"
                className="mb-2 font-medium tracking-wider uppercase"
              >
                Search {activeTabLabel} emails
              </Text>
              <div className="relative">
                <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
                <Input
                  className="pl-9"
                  placeholder={`Search ${activeTabLabel} emails...`}
                  value={listSearch}
                  onChange={(e) => {
                    setListSearch(e.target.value);
                  }}
                  aria-label={`Search ${activeTabLabel} emails`}
                />
              </div>
            </div>
            <div
              className={cn(
                "bg-card min-h-0 min-w-0 flex-1 overflow-x-hidden overflow-y-auto",
                hasNoEmails ? "p-1" : "p-2",
              )}
            >
              {hasNoEmails ? (
                <div className="flex min-h-[120px] min-w-0 flex-col items-center justify-center text-center">
                  <div className="border-border/70 flex w-full min-w-0 flex-col items-center justify-center rounded-xl border border-dashed px-3 py-8 sm:px-5">
                    <Inbox
                      className="text-muted-foreground mb-2 h-6 w-6 shrink-0 opacity-50"
                      aria-hidden
                    />
                    <Text variant="subHeading">No emails yet</Text>
                  </div>
                </div>
              ) : hasNoTabEmails ? (
                <div className="flex min-h-[120px] min-w-0 flex-col items-center justify-center text-center">
                  <div className="border-border/70 flex w-full min-w-0 flex-col items-center justify-center rounded-xl border border-dashed px-3 py-8 sm:px-5">
                    {activeTab === "received" ? (
                      <Inbox
                        className="text-muted-foreground mb-2 h-6 w-6 shrink-0 opacity-50"
                        aria-hidden
                      />
                    ) : (
                      <Send
                        className="text-muted-foreground mb-2 h-6 w-6 shrink-0 opacity-50"
                        aria-hidden
                      />
                    )}
                    <Text variant="subHeading">
                      No {activeTabLabel} emails yet
                    </Text>
                  </div>
                </div>
              ) : filteredRows.length === 0 ? (
                <div className="p-6 text-center">
                  <Text variant="subHeading">
                    No {activeTabLabel} emails match your search.
                  </Text>
                </div>
              ) : (
                <ul className="flex flex-col gap-2">
                  {filteredRows.map((row) => {
                    const isActive = row.id === selectedEmailId;
                    return (
                      <li key={row.id}>
                        <button
                          type="button"
                          className={cn(
                            "bg-card w-full rounded-xl border px-4 py-3 text-left shadow-xs transition-colors",
                            "hover:border-border hover:bg-muted/40",
                            isActive &&
                              "border-primary/30 bg-primary/5 ring-primary/20 ring-1",
                          )}
                          onClick={() => {
                            onSelectedEmailIdChange(row.id);
                          }}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <Text
                              variant="subHeading"
                              color="foreground"
                              as="span"
                              className="min-w-0 flex-1 truncate font-medium"
                            >
                              {listPrimaryLine(row)}
                            </Text>
                            <Text
                              variant="caption"
                              color="muted"
                              as="span"
                              className="shrink-0"
                            >
                              {formatListDate(row)}
                            </Text>
                          </div>
                          <Text
                            variant="body"
                            className="mt-0.5 block truncate font-semibold text-nowrap break-normal"
                          >
                            {row.subject?.trim() || "(No subject)"}
                          </Text>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
          <div className="bg-card min-h-0 min-w-0 overflow-x-hidden overflow-y-auto">
            {hasNoEmails ? (
              <div className="flex h-full min-h-[200px] w-full min-w-0 flex-col items-center justify-center p-3 sm:p-4">
                <div className="border-border/70 w-full max-w-3xl min-w-0 rounded-xl border border-dashed px-5 py-8 text-center sm:px-8 sm:py-10">
                  <Text variant="body" className="font-semibold sm:text-lg">
                    There are no emails for this matter yet.
                  </Text>
                  <Text variant="subHeading" className="mt-2 sm:mt-3">
                    Emails sent to the matter address will appear here when
                    available.
                  </Text>
                </div>
              </div>
            ) : hasNoTabEmails ? (
              <div className="flex h-full min-h-[200px] w-full min-w-0 flex-col items-center justify-center p-3 sm:p-4">
                <div className="border-border/70 w-full max-w-3xl min-w-0 rounded-xl border border-dashed px-5 py-8 text-center sm:px-8 sm:py-10">
                  <Text variant="body" className="font-semibold sm:text-lg">
                    {activeTab === "received"
                      ? "There are no received emails for this matter yet."
                      : "There are no sent emails for this matter yet."}
                  </Text>
                  <Text variant="subHeading" className="mt-2 sm:mt-3">
                    {activeTab === "received"
                      ? "Emails forwarded to the matter address will appear here when available."
                      : "Use the send email button to send a message from this matter."}
                  </Text>
                </div>
              </div>
            ) : selectedEmailId ? (
              <MatterEmailDetailContent
                matterId={matter.id}
                matterEmailId={selectedEmailId}
                matter={matter}
                onDeleteSuccess={() => {
                  onSelectedEmailIdChange(undefined);
                }}
              />
            ) : (
              <div className="flex h-full min-h-[200px] items-center justify-center p-6">
                <Text variant="subHeading">Select an email to read</Text>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MatterEmails;
